/** How a navigation reads to the eye: into a detail, back up, across to a sibling, or not a page change at all. */
export type NavigationKind = 'deeper' | 'shallower' | 'lateral' | 'none'

/** Which way the user walked the browser history (Back, Forward, mouse buttons, keyboard); `none` for a push or replace. */
export type HistoryTravel = 'forward' | 'back' | 'none'

/** The facts about one place in the app that decide a transition. */
export interface NavigationPoint {
  /** Route name and params, never the query: two points with one identity are the same page. */
  identity: string
  /** The route's declared `meta.depth`; section roots are 0, a detail 1. Absent when the route declares none. */
  depth: number | undefined
}

/** Classes that play each kind's enter motion; absent for `none`. */
export const BODY_MOTION_CLASS: Readonly<Record<NavigationKind, string>> = {
  deeper: 'motion-safe:animate-view-deeper',
  shallower: 'motion-safe:animate-view-shallower',
  lateral: 'motion-safe:animate-fade-in',
  none: '',
}

/** Builds a point's identity from a route name (or path when unnamed) and its params. */
export function navigationIdentity(
  name: string | symbol | null | undefined,
  path: string,
  params: Readonly<Record<string, string | readonly string[] | undefined>>,
): string {
  const entries = Object.keys(params)
    .sort()
    .map((key) => `${key}=${String(params[key])}`)
  return `${typeof name === 'string' ? name : path}?${entries.join('&')}`
}

/** Compares the history positions before and after a history walk; a missing one means the direction is unknown, read as `none`. */
export function historyTravel(
  previous: number | null | undefined,
  next: number | null | undefined,
): HistoryTravel {
  if (typeof previous !== 'number' || typeof next !== 'number' || previous === next) return 'none'
  return next > previous ? 'forward' : 'back'
}

/**
 * The transition a navigation plays. Hierarchy decides first (deeper, shallower, lateral); when the
 * user walks the browser history against that hierarchy the walk wins, so Back always mirrors
 * and Forward always drills. Same page or first load plays nothing.
 */
export function navigationKind(
  from: NavigationPoint | null,
  to: NavigationPoint,
  travel: HistoryTravel,
): NavigationKind {
  if (from === null || from.identity === to.identity) return 'none'

  const byDepth = compareDepth(from.depth, to.depth)

  if (travel === 'back' && byDepth !== 'lateral') return 'shallower'
  if (travel === 'forward' && byDepth !== 'lateral') return 'deeper'
  return byDepth ?? 'lateral'
}

function compareDepth(
  from: number | undefined,
  to: number | undefined,
): 'deeper' | 'shallower' | 'lateral' | null {
  if (from === undefined || to === undefined) return null
  if (to > from) return 'deeper'
  if (to < from) return 'shallower'
  return 'lateral'
}
